'use client';
import * as React from 'react';
import {
  ArticleCard,
  type ArticleCardProps,
} from '@/hen-ui/composites/ArticleCard';
import { ImageCard } from '@/hen-ui/composites/ImageCard';
import { Button } from '@/hen-ui/atomics/Button';
import { Icon } from '@/hen-ui/atomics/Icon';
import { SectionHeader } from '@/hen-ui/composites/SectionHeader';
import {
  Carousel,
  CarouselItem,
  type CarouselApi,
} from '@/hen-ui/composites/Carousel';
import { Flex } from '@/hen-ui/composites/Flex';
import { Grid } from '@/hen-ui/composites/Grid';
import { cn } from '@/hen-ui/utils';
import { articleRowContainerVariants } from './article-row.variants';

type BaseArticleRowProps = {
  /** Optional className for container */
  className?: string;
};

type HeroArticleRowProps = BaseArticleRowProps & {
  variant: 'hero';
  /** Full-width background image URL (required for hero variant) */
  heroImage: string;
  /** Eyebrow text */
  heroBrow?: string;
  /** Overlay title */
  heroTitle?: string;
  /** Overlay subtitle */
  heroSubtext?: string;
  /** CTA button configuration */
  heroAction?: {
    label: string;
    onClick?: () => void;
    href?: string;
  };
};

type ContentArticleRowProps = BaseArticleRowProps & {
  variant: '2-col' | '3-col' | '4-col' | 'carousel';
  /** Array of article data matching ArticleCard props */
  articles?: ArticleCardProps[];
  /** Children (ArticleCard components) - alternative to articles prop */
  children?: React.ReactNode;
};

export type ArticleRowProps = HeroArticleRowProps | ContentArticleRowProps;

/**
 * ArticleRow component following HEN Design System standards.
 *
 * A section-level composite component that displays Article Card components
 * in various grid layouts or a full-width Image Card hero with overlay content.
 * Standardizes article collection displays across HEN applications.
 *
 * @example
 * // Hero variant with ImageCard Full Width
 * <ArticleRow
 *   variant="hero"
 *   heroImage="/images/hero-bg.jpg"
 *   heroBrow="Technology"
 *   heroTitle="Discover Your Future"
 *   heroSubtext="Experience an immersive learning journey"
 *   heroAction={{ label: "Learn More", href: "/about" }}
 * />
 *
 * @example
 * // 3-column grid with articles array
 * <ArticleRow
 *   variant="3-col"
 *   articles={[
 *     { image: "/img1.jpg", imageAlt: "Article 1", title: "Title 1", href: "/article-1" },
 *     { image: "/img2.jpg", imageAlt: "Article 2", title: "Title 2", href: "/article-2" },
 *     { image: "/img3.jpg", imageAlt: "Article 3", title: "Title 3", href: "/article-3" },
 *   ]}
 * />
 *
 * @example
 * // 3-column grid with children components
 * <ArticleRow variant="3-col">
 *   <ArticleCard image="/img1.jpg" imageAlt="Article 1" title="Title 1" href="/article-1" />
 *   <ArticleCard image="/img2.jpg" imageAlt="Article 2" title="Title 2" href="/article-2" />
 *   <ArticleCard image="/img3.jpg" imageAlt="Article 3" title="Title 3" href="/article-3" />
 * </ArticleRow>
 *
 * @example
 * // Carousel with side arrows
 * <ArticleRow
 *   variant="carousel"
 *   articles={articles}
 * />
 */
export const ArticleRow = React.forwardRef<HTMLDivElement, ArticleRowProps>(
  (props, ref) => {
    const { variant, className } = props;

    const [carouselApi, setCarouselApi] = React.useState<CarouselApi>();

    // Hero variant renders ImageCard Full Width with overlay content
    if (variant === 'hero') {
      const { heroImage, heroBrow, heroTitle, heroSubtext, heroAction } = props;
      return (
        <Flex
          ref={ref}
          className={cn(articleRowContainerVariants({ variant }), className)}
        >
          <ImageCard
            variant='full-width'
            imageUrl={heroImage}
            imageAlt={heroTitle || 'Hero image'}
          >
            <Flex
              direction='column'
              align='center'
              justify='end'
              gap='lg'
              className='absolute inset-0 bg-image-card-overlay p-d3'
            >
              <SectionHeader
                size='h3'
                align='center'
                color='white'
                brow={heroBrow}
                title={heroTitle || ''}
                subtext={heroSubtext}
              />
              {heroAction && (
                <Button
                  variant='primaryFill'
                  size='sm'
                  onClick={heroAction.onClick}
                  asChild={!!heroAction.href}
                >
                  {heroAction.href ? (
                    <a href={heroAction.href}>{heroAction.label}</a>
                  ) : (
                    heroAction.label
                  )}
                </Button>
              )}
            </Flex>
          </ImageCard>
        </Flex>
      );
    }

    const { articles = [], children } = props;

    // Carousel variant with side-positioned arrows
    if (variant === 'carousel') {
      // Support both children and articles prop
      const carouselContent = children
        ? React.Children.map(children, (child, index) => (
            <CarouselItem
              key={index}
              basis='full'
              className='md:basis-1/2 lg:basis-1/3'
            >
              {child}
            </CarouselItem>
          ))
        : articles.map((article, index) => (
            <CarouselItem
              key={index}
              basis='full'
              className='md:basis-1/2 lg:basis-1/3'
            >
              <ArticleCard {...article} />
            </CarouselItem>
          ));

      return (
        <Flex
          ref={ref}
          className={cn(articleRowContainerVariants({ variant }), className)}
        >
          <Flex direction='row' align='center' gap='md' className='w-full'>
            {/* Left Arrow */}
            <Button
              variant='grayOutline'
              size='sm'
              className='h-8 w-8 min-h-8 min-w-8 shrink-0 p-0 rounded-sm'
              onClick={() => carouselApi?.scrollPrev()}
              disabled={!carouselApi?.canScrollPrev()}
              aria-label='Previous articles'
            >
              <Icon icon='arrow-left' />
            </Button>

            {/* Carousel Content */}
            <Flex className='flex-1 overflow-hidden'>
              <Carousel
                setApi={setCarouselApi}
                opts={{
                  align: 'start',
                  loop: true,
                  slidesToScroll: 1,
                }}
                showControls={false}
                showDots={false}
              >
                {carouselContent}
              </Carousel>
            </Flex>

            {/* Right Arrow */}
            <Button
              variant='grayOutline'
              size='sm'
              className='h-8 w-8 min-h-8 min-w-8 shrink-0 p-0 rounded-sm'
              onClick={() => carouselApi?.scrollNext()}
              disabled={!carouselApi?.canScrollNext()}
              aria-label='Next articles'
            >
              <Icon icon='arrow-right' />
            </Button>
          </Flex>
        </Flex>
      );
    }

    // Grid variants (2-col, 3-col, 4-col)
    return (
      <Flex
        ref={ref}
        className={cn(articleRowContainerVariants({ variant }), className)}
      >
        <Grid
          columns={1}
          columns-md={2}
          columns-lg={
            variant === '3-col' ? 3 : variant === '4-col' ? 4 : undefined
          }
          gap='lg'
          role='list'
          aria-label='Article grid'
        >
          {children
            ? React.Children.map(children, (child, index) => (
                <Flex key={index} role='listitem'>
                  {child}
                </Flex>
              ))
            : articles.map((article, index) => (
                <Flex key={index} role='listitem'>
                  <ArticleCard {...article} />
                </Flex>
              ))}
        </Grid>
      </Flex>
    );
  },
);

ArticleRow.displayName = 'ArticleRow';
